/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { CloseOutlined, DownOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Dropdown,
  Input,
  type InputRef,
  type MenuProps,
  Popconfirm,
  Row,
  Space,
} from "antd";
import Table, { type ColumnsType } from "antd/lib/table";
import { type FilterValue } from "antd/lib/table/interface";
import React, { type ChangeEvent, useEffect, useRef, useState } from "react";
import TableHeader from "../components/SearchFilter";
import { Link, useSearchParams } from "react-router-dom";
import {
  type PaginationParams,
  type IssueType,
  IssueStatus,
  SourceType,
} from "../redux/slices/midservice/midserviceTypes";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import {
  issuesListAsync,
  selectIssuesList,
  setIssuesListCurrentPage,
} from "../redux/slices/midservice/midserviceSlice";
import { IssueStatusTag } from "../components/StatusTag";
import {
  findFilterInSearch,
  addfiltersToSearchString,
} from "../utils/tableUtils";
import { timestampToReadable } from "../utils/time";
import { API } from "../api/Api";
import useWebSocket from "react-use-websocket";
import { getApiUrlWS } from "../utils/url";
import { selectCurrentUser } from "../redux/slices/user/userSlice";

const IssuesPage: React.FC = () => {
  return <IssuesTable />;
};

interface EventIds {
  id: string;
  title: string;
}

interface IssueTableProps {
  startSearch?: string | null;
}

const IssuesTable: React.FC<IssueTableProps> = ({
  startSearch = "status:!INCORRECT and status:!DELETED",
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const page: number = Number(searchParams.get("page") ?? 1);
  const pageSize: number = Number(searchParams.get("limit") ?? 10);
  const issuesList = useAppSelector(selectIssuesList);
  const search: string | null = searchParams.get("search") ?? startSearch;
  const [searchString, setSearchString] = useState<string>(search ?? "");
  const searchInputRef = useRef<InputRef | null>(null);
  const [eventIds, setEventIds] = useState<EventIds[]>([]);

  useWebSocket(`${getApiUrlWS()}/api/v1/issue/ws/updates?&user_id=${user.id}`, {
    shouldReconnect: () => true,
    reconnectAttempts: 5,
    reconnectInterval: 3,
    onMessage() {
      const instancesListParams: PaginationParams = {
        page,
        limit: pageSize,
        search: searchString,
      };
      dispatch(issuesListAsync(instancesListParams));
    },
  });

  const handleHeaderClick = (): void => {
    searchInputRef.current?.focus();
  };

  useEffect(() => {
    if (searchParams.get("page") == null || searchParams.get("limit") == null) {
      setSearchParams({
        page: "1",
        limit: "10",
        ...Object.fromEntries(searchParams),
      });
    }
    const instancesListParams: PaginationParams = {
      page,
      limit: pageSize,
      search,
    };
    dispatch(issuesListAsync(instancesListParams));
  }, []);

  const searchToFilters = (
    search: string | null,
  ): Record<string, FilterValue | null> => {
    const filters: Record<string, FilterValue | null> = {
      domain: null,
      title: null,
      issueStatus: null,
      sourceType: null,
    };

    if (search == null) {
      return filters;
    }

    filters.domain = findFilterInSearch(
      "domain",
      ["gamma", "partner", "orbit"],
      search,
    );
    filters.title = findFilterInSearch(
      "title",
      eventIds.map((event) => event.title),
      search,
    );
    filters.issueStatus = findFilterInSearch(
      "status",
      Object.keys(IssueStatus),
      search,
    );
    filters.sourceType = findFilterInSearch(
      "source_type",
      Object.keys(SourceType),
      search,
    );

    return filters;
  };

  const createSearchString = (mnemonic: string): void => {
    setSearchString((val: string) => {
      if (val.trim() === "") {
        return `${mnemonic}:`;
      }
      if (val.includes(mnemonic)) {
        return val;
      }
      return val + ` and ${mnemonic}:`;
    });
    handleHeaderClick();
  };

  const [filter, setFilter] = useState<Record<string, FilterValue | null>>(
    searchToFilters(search),
  );

  useEffect(() => {
    async function getEventsIds(): Promise<void> {
      const { data } = await API.getEventsIds();
      setEventIds(data);
    }

    getEventsIds();
  }, []);

  const updateIssueStatus = async (
    issueId: number,
    newStatus: string,
  ): Promise<void> => {
    try {
      const response = await API.setIssueStatus(issueId, newStatus);

      console.log("Статус успешно обновлён:", response);
    } catch (error) {
      console.error("Ошибка при обновлении статуса:", error);
    }
  };

  const columns: ColumnsType<IssueType> = [
    {
      title: (
        <TableHeader
          title="Идентификатор"
          tooltipTitle="id:<текст>"
          onClickFn={createSearchString}
          mnemonic="id"
        ></TableHeader>
      ),
      dataIndex: "id",
      key: "id",
      render: (text, record) => (
        <Space size="middle">
          <Link to={`/issues/${record.id}`} key="list-loadmore-edit">
            {text}
          </Link>
        </Space>
      ),
    },
    {
      title: (
        <TableHeader
          title="Домен"
          tooltipTitle="domain:<gamma>, <partner> или <orbit>"
          onClickFn={createSearchString}
          mnemonic="domain"
          additionalIcon={false}
        ></TableHeader>
      ),
      dataIndex: "domain",
      key: "domain",
      filters: [
        {
          text: "gamma",
          value: "gamma",
        },
        {
          text: "partner",
          value: "partner",
        },
        {
          text: "orbit",
          value: "orbit",
        },
      ],
      filteredValue: filter.domain ?? null,
    },
    {
      title: (
        <TableHeader
          title="Событие"
          tooltipTitle="title:"
          onClickFn={createSearchString}
          mnemonic="title"
          additionalIcon={false}
        ></TableHeader>
      ),
      dataIndex: "title",
      key: "title",
      filters: eventIds.map((eventId) => ({
        text: eventId.title,
        value: eventId.title,
      })),
      filteredValue: filter.title ?? null,
    },
    {
      title: (
        <TableHeader
          title="Тип источника"
          tooltipTitle="source_type:"
          onClickFn={createSearchString}
          mnemonic="source_type"
          additionalIcon={false}
        ></TableHeader>
      ),
      dataIndex: "sourceType",
      key: "sourceType",
      filters: Object.entries(SourceType).map(([key, value]) => ({
        text: value,
        value: key,
      })),
      filteredValue: filter.sourceType ?? null,
      render: (value: keyof typeof SourceType) => SourceType[value],
    },
    {
      title: (
        <TableHeader
          title="Источник"
          tooltipTitle="source_id:"
          onClickFn={createSearchString}
          mnemonic="source_id"
        ></TableHeader>
      ),
      dataIndex: "sourceId",
      key: "sourceId",
      render: (text, record) => (
        <Space size="middle">
          <Link
            to={`/instances/${
              record.sourceType === "TRADEPOINT"
                ? `tt/${record.domain}/${record.sourceId}`
                : record.instanceId
            }`}
            key="list-loadmore-edit"
          >
            {record.sourceType === "TRADEPOINT"
              ? `${record.networkName}, ${record.addressName}`
              : text}
          </Link>
        </Space>
      ),
    },
    {
      title: (
        <TableHeader
          title="Дата"
          tooltipTitle="created_at:"
          onClickFn={createSearchString}
          mnemonic="created_at"
        ></TableHeader>
      ),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => timestampToReadable(text),
    },
    {
      title: (
        <TableHeader
          title="Интенсивность"
          tooltipTitle="event_count:"
          onClickFn={createSearchString}
          mnemonic="event_count"
        ></TableHeader>
      ),
      dataIndex: "eventCount",
      key: "eventCount",
    },
    {
      title: (
        <TableHeader
          title="Статус"
          tooltipTitle="status:"
          onClickFn={createSearchString}
          mnemonic="status"
          additionalIcon={false}
        ></TableHeader>
      ),
      dataIndex: "status",
      key: "issueStatus",
      filters: Object.entries(IssueStatus).map(([key, value]) => ({
        text: value,
        value: key,
      })),
      filteredValue: filter.issueStatus ?? null,
      render: (value) => <IssueStatusTag status={value} />,
    },
    {
      title: "Управление",
      key: "actionIssueStatus",
      fixed: "right",
      width: 100,
      render: (record) => {
        const items: MenuProps["items"] = Object.entries(IssueStatus)
          .filter(([key]) => key !== record.status)
          .map(([status, label]) => {
            const requiresConfirmation = [
              IssueStatus.DELETED,
              IssueStatus.INCORRECT,
            ].includes(IssueStatus[status as keyof typeof IssueStatus]);
            return {
              key: status,
              label: requiresConfirmation ? (
                <Popconfirm
                  title="Изменить статус?"
                  onConfirm={() => {
                    updateIssueStatus(record.id, status);
                  }}
                  okText="Да"
                  cancelText="Нет"
                >
                  <div>{label}</div>
                </Popconfirm>
              ) : (
                <div
                  onClick={() => {
                    updateIssueStatus(record.id, status);
                  }}
                >
                  {label}
                </div>
              ),
            };
          });

        return (
          <Dropdown menu={{ items }}>
            <Button>
              Изменить статус <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const onTableChange = (
    pagination: any,
    filters: Record<string, FilterValue | null>,
    sorter: any,
    extra: any,
  ): void => {
    console.log("onTableChange", pagination, filters, sorter, extra);
    console.log("filter onTableChange before", filters);

    const searchParams: Record<string, string> = {
      page: pagination.current,
      limit: pagination.pageSize,
    };

    const currentSearch: string = addfiltersToSearchString(
      filters,
      searchString,
    );

    if (currentSearch !== "") {
      searchParams.search = currentSearch;
    }

    const issueListParams: PaginationParams = {
      page: pagination.current,
      limit: pagination.pageSize,
      search: currentSearch,
    };
    dispatch(issuesListAsync(issueListParams));

    dispatch(setIssuesListCurrentPage(pagination.current));

    const newFilter: Record<string, FilterValue | null> = {
      ...filters,
    };
    setFilter(newFilter);
    setSearchString(currentSearch);
    setSearchParams(searchParams);

    console.log("filter onTableChange after", newFilter);
  };

  const onSearchChange = (value: ChangeEvent<HTMLInputElement>): void => {
    const currentSearch = value.target.value;
    console.log("search", currentSearch);
    setSearchString(currentSearch);

    const filters = searchToFilters(currentSearch);
    setFilter(filters);

    const instancesListParams: PaginationParams = {
      page: 1,
      limit: pageSize,
      search: currentSearch,
    };
    dispatch(issuesListAsync(instancesListParams));

    const newSearchParams: Record<string, string> = {
      ...Object.fromEntries(searchParams),
      search: currentSearch,
    };
    if (newSearchParams.search === "") delete newSearchParams.search;
    setSearchParams(newSearchParams);
  };

  const dropFilters = (): void => {
    const instancesListParams: PaginationParams = {
      page: 1,
      limit: pageSize,
      filter: null,
      search: startSearch ?? "",
    };
    dispatch(issuesListAsync(instancesListParams));

    setFilter(searchToFilters(startSearch));

    setSearchString(startSearch ?? "");
    setSearchParams({
      page: "1",
      limit: pageSize.toString(),
    });
  };

  const getTableDataSource = (): any[] => {
    console.log("issuesList.content", issuesList.content);
    return issuesList.content.map((item: IssueType) => ({
      ...item,
      key: item.id,
    }));
  };

  return (
    <>
      <Row style={{ marginBottom: "1rem" }}>
        <Col flex="auto">
          <Input
            ref={searchInputRef}
            addonBefore={<SearchOutlined />}
            placeholder="Поиск по всем полям"
            allowClear
            onChange={onSearchChange}
            value={searchString}
          />
        </Col>
        <Col span={6} offset={1} flex="none">
          <Button onClick={dropFilters} icon={<CloseOutlined />}>
            Сбросить фильтры
          </Button>
        </Col>
      </Row>
      <Table
        size="small"
        onChange={onTableChange}
        onRow={(issue, index) => {
          if (typeof index !== "undefined") {
            const attr: any = {
              index,
              issue,
            };
            return attr as React.HTMLAttributes<any>;
          }
          return {} satisfies React.HTMLAttributes<any>;
        }}
        pagination={{
          showSizeChanger: true,
          pageSize: issuesList.pageSize,
          total: issuesList.totalRecord,
          current: issuesList.pageNumber,
        }}
        scroll={{ x: true }}
        columns={columns}
        dataSource={getTableDataSource()}
      />
    </>
  );
};

export default IssuesPage;
